import React from "react";
import PropTypes from "prop-types";
import { FormControlLabel, Switch as MuiSwitch } from "@mui/material";
import { UIFieldShell, useUIField } from "../form";

/**
 * @typedef {object} UIToggleProps
 * @property {string} [id]
 * @property {string} [name]
 * @property {boolean} [checked]
 * @property {import("react").ReactNode} [label]
 * @property {"bottom"|"end"|"start"|"top"} [labelPlacement]
 * @property {boolean} [required]
 * @property {import("react").ReactNode} [helperText]
 * @property {boolean} [error]
 * @property {boolean} [disabled]
 * @property {(checked: boolean, event: import("react").SyntheticEvent) => void} [onChange]
 * @property {(event: import("react").FocusEvent) => void} [onBlur]
 * @property {string} [className]
 */

/** @type {import("react").FC<UIToggleProps>} */
const UIToggle = ({
  id,
  name,
  checked,
  label = null,
  labelPlacement = "end",
  required = false,
  helperText = null,
  error,
  disabled = false,
  onChange,
  onBlur,
  className = "",
  ...props
}) => {
  const fieldId = id || name;
  const field = useUIField({
    name,
    checked,
    error,
    helperText,
  });

  const toggleClassName = ["ui-toggle", className].filter(Boolean).join(" ");
  const control = (
    <MuiSwitch
      id={fieldId}
      name={name}
      checked={field.checked}
      disabled={disabled}
      required={required}
      className={toggleClassName}
      onChange={(event, nextChecked) => {
        field.setValue(nextChecked);
        onChange?.(nextChecked, event);
      }}
      onBlur={(event) => {
        field.setTouched(true);
        onBlur?.(event);
      }}
      inputProps={{
        "aria-label": typeof label === "string" ? label : name || "Toggle",
      }}
      {...props}
    />
  );

  return (
    <UIFieldShell
      id={fieldId}
      helperText={field.helperText}
      error={field.showError}
    >
      {label ? (
        <FormControlLabel
          className="ui-toggle-label"
          control={control}
          label={label}
          labelPlacement={labelPlacement}
          disabled={disabled}
        />
      ) : (
        control
      )}
    </UIFieldShell>
  );
};

UIToggle.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.node,
  labelPlacement: PropTypes.oneOf(["bottom", "end", "start", "top"]),
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
};

export default UIToggle;
